import { useState, useEffect } from 'react';
import { useFunnels, type Funnel } from '../../../hooks/useFunnels';
import { useFunnelStages } from '../../../hooks/useFunnelStages';
import { supabase } from '../../../lib/supabase';
import { useActivityLog } from '../../../hooks/useActivityLog';

interface FunnelManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FunnelStats {
  stageCount: number;
  dealCount: number;
}

export default function FunnelManagerModal({ isOpen, onClose }: FunnelManagerModalProps) {
  const { funnels, createFunnel, updateFunnel, deleteFunnel, reloadFunnels } = useFunnels();
  const { logActivity } = useActivityLog();
  const [stats, setStats] = useState<Map<string, FunnelStats>>(new Map());
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#3b82f6',
  });
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    color: '#3b82f6',
  });
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const [moveToFunnelId, setMoveToFunnelId] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      loadStats();
    }
  }, [isOpen, funnels]);

  const loadStats = async () => {
    const newStats = new Map<string, FunnelStats>();
    
    for (const funnel of funnels) {
      const [stagesRes, dealsRes] = await Promise.all([
        supabase.from('funnel_stages').select('id', { count: 'exact', head: true }).eq('funnel_id', funnel.id),
        supabase.from('deals').select('id', { count: 'exact', head: true }).eq('funnel_id', funnel.id),
      ]);

      newStats.set(funnel.id, {
        stageCount: stagesRes.count || 0,
        dealCount: dealsRes.count || 0,
      });
    }

    setStats(newStats);
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) return;

    const result = await createFunnel({
      name: formData.name,
      description: formData.description,
      color: formData.color,
      is_default: funnels.length === 0,
    });

    if (result.success && result.data) {
      // Criar etapas padrão
      const defaultStages = [
        { id: 'sem_contato', label: 'Sem contato', color: '#94a3b8', sort_order: 0, is_fixed: false },
        { id: 'contato_feito', label: 'Contato feito', color: '#3b82f6', sort_order: 1, is_fixed: false },
        { id: 'won', label: 'Ganho', color: '#10b981', sort_order: 2, is_fixed: true },
        { id: 'lost', label: 'Perdido', color: '#ef4444', sort_order: 3, is_fixed: true },
      ];

      await supabase.from('funnel_stages').insert(
        defaultStages.map(s => ({
          ...s,
          funnel_id: result.data.id,
        }))
      );

      await logActivity({
        action: 'create',
        module: 'funnels',
        entityId: result.data.id,
        entityName: formData.name,
        details: { data: formData }
      });

      setFormData({ name: '', description: '', color: '#3b82f6' });
      setIsCreating(false);
      await reloadFunnels();
      await loadStats();
    }
  };

  const handleDuplicate = async (sourceFunnelId: string) => {
    const sourceFunnel = funnels.find(f => f.id === sourceFunnelId);
    if (!sourceFunnel) return;

    setDuplicatingId(sourceFunnelId);

    // Criar novo funil
    const result = await createFunnel({
      name: `${sourceFunnel.name} (cópia)`,
      description: sourceFunnel.description,
      color: sourceFunnel.color,
      is_default: false,
    });

    if (result.success && result.data) {
      // Copiar etapas
      const { data: sourceStages } = await supabase
        .from('funnel_stages')
        .select('*')
        .eq('funnel_id', sourceFunnelId)
        .order('sort_order');

      if (sourceStages) {
        await supabase.from('funnel_stages').insert(
          sourceStages.map(s => ({
            id: s.id,
            label: s.label,
            color: s.color,
            sort_order: s.sort_order,
            is_fixed: s.is_fixed,
            funnel_id: result.data.id,
          }))
        );
      }

      await reloadFunnels();
      await loadStats();
    }

    setDuplicatingId(null);
  };

  const handleSetDefault = async (funnelId: string) => {
    await updateFunnel(funnelId, { is_default: true });
    await reloadFunnels();
  };

  const handleEdit = (funnel: Funnel) => {
    setEditingId(funnel.id);
    setEditFormData({
      name: funnel.name,
      description: funnel.description || '',
      color: funnel.color,
    });
  };

  const handleSaveEdit = async () => {
    if (!editingId || !editFormData.name.trim()) return;

    const originalFunnel = funnels.find(f => f.id === editingId);
    
    await updateFunnel(editingId, {
      name: editFormData.name,
      description: editFormData.description,
      color: editFormData.color,
    });

    if (originalFunnel) {
      await logActivity({
        action: 'update',
        module: 'funnels',
        entityId: editingId,
        entityName: editFormData.name,
        details: { before: originalFunnel, after: editFormData }
      });
    }

    setEditingId(null);
    await reloadFunnels();
  };

  const handleDelete = async () => {
    if (!deletingId) return;

    const funnelToDelete = funnels.find(f => f.id === deletingId);
    const funnelStats = stats.get(deletingId);
    
    if (funnelStats && funnelStats.dealCount > 0 && moveToFunnelId) {
      // Mover deals para outro funil
      await supabase
        .from('deals')
        .update({ funnel_id: moveToFunnelId })
        .eq('funnel_id', deletingId);
    }

    await deleteFunnel(deletingId);
    
    if (funnelToDelete) {
      await logActivity({
        action: 'delete',
        module: 'funnels',
        entityId: deletingId,
        entityName: funnelToDelete.name,
        details: { 
          deletedData: funnelToDelete,
          dealsMoved: funnelStats?.dealCount || 0,
          movedToFunnelId: moveToFunnelId || null
        }
      });
    }
    
    setDeletingId(null);
    setMoveToFunnelId('');
    await reloadFunnels();
    await loadStats();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-brand-50 rounded-xl flex items-center justify-center">
              <i className="ri-stack-line text-brand-600 text-xl"></i>
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Gerenciar Funis</h2>
              <p className="text-xs text-gray-500">Organize seus processos de vendas</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-gray-400 text-xl"></i>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Create new funnel */}
          {!isCreating ? (
            <button
              onClick={() => setIsCreating(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-200 rounded-xl hover:border-brand-300 hover:bg-brand-50/30 transition-all cursor-pointer text-gray-600 hover:text-brand-600 mb-4"
            >
              <i className="ri-add-line text-xl"></i>
              <span className="text-sm font-medium">Novo Funil</span>
            </button>
          ) : (
            <div className="bg-gray-50 rounded-xl p-4 mb-4 border border-gray-200">
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Nome do funil *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: Vendas B2B, Parcerias, etc."
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Descrição (opcional)
                  </label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Descreva o objetivo deste funil"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Cor
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      className="w-12 h-10 rounded-lg border border-gray-200 cursor-pointer"
                    />
                    <span className="text-xs text-gray-500">{formData.color}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 pt-2">
                  <button
                    onClick={handleCreate}
                    disabled={!formData.name.trim()}
                    className="flex-1 px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Criar Funil
                  </button>
                  <button
                    onClick={() => {
                      setIsCreating(false);
                      setFormData({ name: '', description: '', color: '#3b82f6' });
                    }}
                    className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Funnels list */}
          <div className="space-y-3">
            {funnels.map((funnel) => {
              const funnelStats = stats.get(funnel.id);
              const isEditing = editingId === funnel.id;

              return (
                <div
                  key={funnel.id}
                  className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow"
                >
                  {isEditing ? (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1.5">
                          Nome do funil *
                        </label>
                        <input
                          type="text"
                          value={editFormData.name}
                          onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1.5">
                          Descrição
                        </label>
                        <input
                          type="text"
                          value={editFormData.description}
                          onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1.5">
                          Cor
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={editFormData.color}
                            onChange={(e) => setEditFormData({ ...editFormData, color: e.target.value })}
                            className="w-12 h-10 rounded-lg border border-gray-200 cursor-pointer"
                          />
                          <span className="text-xs text-gray-500">{editFormData.color}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={handleSaveEdit}
                          disabled={!editFormData.name.trim()}
                          className="flex-1 px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
                        >
                          Salvar
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-start gap-3 flex-1">
                          <div
                            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: `${funnel.color}20` }}
                          >
                            <i
                              className="ri-stack-line text-lg"
                              style={{ color: funnel.color }}
                            ></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-sm font-bold text-gray-900">{funnel.name}</h3>
                              {funnel.is_default && (
                                <span className="px-2 py-0.5 bg-amber-50 text-amber-700 text-[10px] font-medium rounded-full whitespace-nowrap">
                                  Padrão
                                </span>
                              )}
                            </div>
                            {funnel.description && (
                              <p className="text-xs text-gray-500 mb-2">{funnel.description}</p>
                            )}
                            <div className="flex items-center gap-4 text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                <i className="ri-list-check text-sm"></i>
                                {funnelStats?.stageCount || 0} etapas
                              </span>
                              <span className="flex items-center gap-1">
                                <i className="ri-briefcase-line text-sm"></i>
                                {funnelStats?.dealCount || 0} negociações
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                        {!funnel.is_default && (
                          <button
                            onClick={() => handleSetDefault(funnel.id)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-star-line"></i>
                            Definir como padrão
                          </button>
                        )}
                        <button
                          onClick={() => handleEdit(funnel)}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-edit-line"></i>
                          Editar
                        </button>
                        <button
                          onClick={() => handleDuplicate(funnel.id)}
                          disabled={duplicatingId === funnel.id}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                        >
                          <i className={duplicatingId === funnel.id ? 'ri-loader-4-line animate-spin' : 'ri-file-copy-line'}></i>
                          Duplicar
                        </button>
                        {funnels.length > 1 && (
                          <button
                            onClick={() => setDeletingId(funnel.id)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap ml-auto"
                          >
                            <i className="ri-delete-bin-line"></i>
                            Excluir
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Delete confirmation modal */}
      {deletingId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center">
                <i className="ri-alert-line text-red-600 text-2xl"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Excluir Funil</h3>
                <p className="text-sm text-gray-500">Esta ação não pode ser desfeita</p>
              </div>
            </div>

            {stats.get(deletingId)?.dealCount ? (
              <div className="mb-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                  <p className="text-sm text-amber-800">
                    Este funil possui <strong>{stats.get(deletingId)?.dealCount} negociações</strong>.
                    Escolha para onde movê-las:
                  </p>
                </div>
                <select
                  value={moveToFunnelId}
                  onChange={(e) => setMoveToFunnelId(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 cursor-pointer"
                >
                  <option value="">Selecione um funil</option>
                  {funnels
                    .filter((f) => f.id !== deletingId)
                    .map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.name}
                      </option>
                    ))}
                </select>
              </div>
            ) : (
              <p className="text-sm text-gray-600 mb-4">
                Tem certeza que deseja excluir este funil? Esta ação não pode ser desfeita.
              </p>
            )}

            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setDeletingId(null);
                  setMoveToFunnelId('');
                }}
                className="flex-1 px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                disabled={stats.get(deletingId)?.dealCount ? !moveToFunnelId : false}
                className="flex-1 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
              >
                Excluir Funil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
